// src/Pages/Cart/CartPage.js

import React, { useState, useEffect, useRef } from "react";
// 🔑 Import writeBatch and query for efficient deletion and fetching
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import "./Cart.css";
import { FaShoppingCart, FaTrashAlt, FaTimes } from "react-icons/fa";

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popupImage, setPopupImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const navigate = useNavigate();

  // Constant for 24 hours in milliseconds
  const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

  // useEffect hook to fetch and clean data from Firestore
  useEffect(() => {
    const fetchAndCleanCartItems = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "carts"));
        const items = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const now = Date.now();
        const itemsToDelete = [];
        const activeItems = [];

        // 🔑 SEPARATE ACTIVE ITEMS FROM EXPIRED ITEMS
        items.forEach((item) => {
          const itemCreationTime = item.createdAt?.toMillis() || 0;

          if (now - itemCreationTime >= TWENTY_FOUR_HOURS_MS) {
            itemsToDelete.push(item);
          } else {
            activeItems.push(item);
          }
        });

        // 🔑 FIREBASE CLEANUP: Delete expired items using a batch
        if (itemsToDelete.length > 0) {
          const batch = writeBatch(db);

          itemsToDelete.forEach((item) => {
            const docRef = doc(db, "carts", item.id);
            batch.delete(docRef);
          });

          await batch.commit();
          console.log(
            `✅ Automatically deleted ${itemsToDelete.length} expired cart items from Firestore.`
          );
        }

        // Update state with only the active items
        setCartItems(activeItems);
      } catch (error) {
        console.error("Error fetching and cleaning cart items:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAndCleanCartItems();
    // ⚠️ Run only once on mount, or include dependencies that trigger re-fetch/clean
  }, []);

  useEffect(() => {
    if (popupImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [popupImage]);

  // Updated handleDelete to delete from Firestore
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "carts", id));
      setCartItems(cartItems.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  // Updated function to handle navigation to Checkout
  const handlePlaceOrder = () => {
    // Navigate to the order page, passing ALL current cart items as state
    navigate("/Checkout", { state: { itemsToOrder: cartItems } });
  };

  // ... (rest of the image zoom/pan functions remain the same) ...
  const openPopup = (image) => {
    setPopupImage(image);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const closePopup = () => {
    setPopupImage(null);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.min(Math.max(prev + delta, 1), 5));
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch drag for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.touches[0].clientX - position.x,
      y: e.touches[0].clientY - position.y,
    };
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.touches[0].clientX - dragStart.current.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchEnd = () => setIsDragging(false);

  return (
    <div className="cart-page">
      {/* Banner */}
      <section className="cart-banner">
        <FaShoppingCart className="cart-icon" />
        <h1>Your Luxury Cart</h1>
        <p>Refined choices, crafted for elegance.</p>
      </section>

      {/* Cart Grid */}
      <section className="cart-grid">
        {loading ? (
          <p className="loading-message">Loading cart...</p>
        ) : cartItems.length > 0 ? (
          cartItems.map((item) => (
            <div className="cart-card" key={item.id}>
              <div
                className="img-wrapper"
                onClick={() => openPopup(item.image)}
              >
                <img src={item.image} alt={item.productName} />
              </div>
              <div className="cart-details">
                <h3>{item.productName}</h3>
                <p>
                  <strong>Weight:</strong> {item.weight} gm
                </p>
                <p>
                  <strong>Size:</strong> {item.size || "-"}
                </p>
                <p>
                  <strong>Carat:</strong> {item.carat}
                </p>
              </div>
              <button
                className="delete-btn"
                onClick={() => handleDelete(item.id)}
              >
                <FaTrashAlt />
              </button>
            </div>
          ))
        ) : (
          <p className="empty-cart">Your cart is empty.</p>
        )}
      </section>

      {/* Place Order Button */}
      {cartItems.length > 0 && (
        <div className="cart-footer">
          <button className="order-btn" onClick={handlePlaceOrder}>
            Place Order
          </button>
        </div>
      )}

      {/* Popup Modal (Image Zoom) */}
      {popupImage && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closePopup}>
              <FaTimes />
            </button>
            <div
              className="popup-img-wrapper"
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img
                src={popupImage}
                alt="Zoomed Product"
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                  cursor: isDragging ? "grabbing" : "grab",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
